import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { authLog, authError } from '@/utils/logger'
import { performRefresh } from '@/services/api'

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/EmailClientView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/preferences',
      name: 'preferences',
      component: () => import('@/views/PreferencesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/compose',
      name: 'compose',
      component: () => import('@/views/ComposeView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: '/dashboard'
    }
  ]
})

// Helper function to check if JWT token is locally expired
function isLocallyExpired(token: string): boolean {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return true
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    const exp = payload?.exp
    if (typeof exp !== 'number') return true
    return exp < Math.floor(Date.now() / 1000) - 30 // 30s skew
  } catch {
    return true
  }
}

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Enhanced logging for navigation
  authLog(`Router navigation: ${from.path} → ${to.path}`, {
    requiresAuth: to.meta.requiresAuth,
    requiresGuest: to.meta.requiresGuest,
    isAuthenticated: authStore.isAuthenticated
  })

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    const accessToken = localStorage.getItem('access_token')

    // If no token, redirect to login
    if (!accessToken) {
      authLog(`Protected route requires auth, no token found, redirecting to login: ${to.path}`)
      next({ path: '/login', query: { reason: 'expired' } })
      return
    }

    // If token is still valid, allow navigation
    if (!isLocallyExpired(accessToken)) {
      authLog(`Access token is valid for ${to.path}`)
      // Continue to check other guards
    } else {
      // Token is locally expired, attempt refresh
      authLog(`Access token locally expired, attempting refresh for ${to.path}`)
      try {
        await performRefresh()
        authLog(`Token refresh succeeded for ${to.path}`)
        // Continue to check other guards
      } catch (error) {
        authError(`Token refresh failed for ${to.path}`, error)
        next({ path: '/login', query: { reason: 'expired' } })
        return
      }
    }
  }

  // Check if route requires guest (not authenticated)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    authLog(`Guest route accessed while authenticated, redirecting to dashboard: ${to.path}`)
    next('/dashboard')
    return
  }

  authLog(`Navigation allowed: ${to.path}`)
  next()
})

// Log navigation errors
router.onError((error) => {
  authError('Router navigation error', {
    message: error.message,
    stack: error.stack
  })
})

export default router
